<template>
    <div>
        <template v-if="items.length">
            <!-- Antragsteller/Versicherungsnehmer -->
            <div class="mb-4">
                <div class="font-xl-links-bold mb-3">
                    Antragsteller/Versicherungsnehmer
                </div>
                <div class="font-xl-links">
                    <template v-if="hasVN">
                        <template v-if="vnPerson.vorname && vnPerson.nachname">
                            {{ vnPerson.vorname }} {{ vnPerson.nachname }}
                            <template v-if="vnPerson.geburtstag && isValidGeburtstag(vnPerson.geburtstag)">
                                (geb. {{ formatiertDatum(vnPerson.geburtstag) }})
                            </template>
                            <template v-else>
                                (bitte das Geburtsdatum angeben)
                            </template>
                        </template>
                        <template v-else>
                            Bitte ergänzen Sie den Namen
                        </template>
                    </template>
                    <template v-else>
                        Im folgenden Schritt ergänzen Sie die Angaben zum Antragsteller/Versicherungsnehmer.
                    </template>
                </div>
            </div>

            <!-- Überschrift Versicherte Person(en) -->
            <div class="font-xl-links-bold mb-3">
                {{ items.length === 1 ? 'Versicherte Person' : 'Versicherte Personen' }}
            </div>

            <!-- Versicherte Personen -->
            <div v-for="(item, index) in items" :key="item.id" :class="{ 'mb-3': index !== items.length - 1 }">
                <div class="font-xl-links">
                    <template v-if="item.vorname && item.nachname">
                        {{ item.vorname }} {{ item.nachname }}
                        <template v-if="item.geburtstag && isValidGeburtstag(item.geburtstag)">
                            (geb. {{ formatiertDatum(item.geburtstag) }})
                        </template>
                        <template v-else>
                            (bitte das Geburtsdatum angeben)
                        </template>
                    </template>
                    <template v-else>
                        {{ index + 1 }}. Person: Bitte ergänzen Sie den Namen
                    </template>
                </div>
                <div class="row mt-3">
                    <template v-for="tarif in getTarifeForItem(item)" :key="tarif.id">
                        <div class="col-12 col-md-6 font-xl-links">
                            {{ tarif.name }}
                        </div>
                        <div class="col-12 col-md-6 font-xl-rechts-bold">
                            {{ formatTarifBeitrag(tarif.beitrag) }}
                        </div>
                    </template>
                </div>
                <hr v-if="index !== items.length - 1">
            </div>
            
            <!-- Horizontaler Trenner mit Abstand vor Gesamtbeitrag -->
            <hr class="mt-4">
            
            <!-- Gesamtbeitrag -->
            <div class="row">
                <div class="col-12 col-md-6 font-xl-links">
                    Monatlicher Gesamtbeitrag
                </div>
                <div class="col-12 col-md-6 font-xl-rechts-bold">
                    {{ formatGesamtbetrag }}
                </div>
            </div>
        </template>
        <template v-else>
            <div class="font-xl-links text-center py-4">
                Bitte fügen Sie zu versichernde Personen hinzu und wählen Sie Tarife aus.
            </div>
        </template>
    </div>
</template>

<script setup>
import { computed } from 'vue';
import { formatiertDatum, getTarifName, isValidDateInPast } from '@/helpers/hilfsfunktionen';

const props = defineProps({
    items: {
        type: Array,
        default: () => []
    },
    gesamt: {
        type: String,
        default: '- €'
    },
    vndaten: {
        type: Object,
        default: () => ({ id: 'initial' })
    }
});

// Prüft, ob eine Person als VN ausgewählt wurde
const hasVN = computed(() => {
    return props.vndaten.id !== 'initial' && props.vndaten.id !== 'vn0';
});

// Findet die VN-Person in den Items (falls ausgewählt)
const vnPerson = computed(() => {
    if (!hasVN.value) {
        return null;
    }
    return props.items.find(item => item.id === props.vndaten.id) || props.vndaten;
});

const isValidGeburtstag = (datum) => {
    return datum && isValidDateInPast(datum);
};

const getTarifeForItem = (item) => {
    const tarife = [];
    if (item.tarif1 === 1) tarife.push({ id: 1, name: getTarifName(1), beitrag: item.tarif1beitragStr });
    if (item.tarif2 === 1) tarife.push({ id: 2, name: getTarifName(2), beitrag: item.tarif2beitragStr });
    if (item.tarif3 === 1) tarife.push({ id: 3, name: getTarifName(3), beitrag: item.tarif3beitragStr });
    if (item.tarif4 === 1) tarife.push({ id: 4, name: getTarifName(4), beitrag: item.tarif4beitragStr });
    if (item.tarif5 === 1) tarife.push({ id: 5, name: getTarifName(5), beitrag: item.tarif5beitragStr });
    return tarife;
};

const formatTarifBeitrag = (beitrag) => {
    if (!beitrag || beitrag.startsWith('Bitte')) {
        return beitrag || '- €';
    }
    return beitrag.includes('mtl.') ? beitrag : `${beitrag} mtl.`;
};

const formatGesamtbetrag = computed(() => {
    if (!props.gesamt || props.gesamt.startsWith('Bitte')) {
        return props.gesamt || '- €';
    }
    return props.gesamt.includes('mtl.') ? props.gesamt : `${props.gesamt} mtl.`;
});
</script>